import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { MaskLine } from "@/components/site/motion";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";

const inputCls = "h-14 w-full border-b border-bone/25 bg-transparent text-base text-bone outline-none transition-colors placeholder:text-smoke focus:border-acid";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form.email, form.password);
      navigate("/admin");
    } catch (err) {
      setError(errorMessage(err, "Accesso non riuscito. Riprova."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="grid min-h-screen lg:grid-cols-2" data-testid="admin-login-page">
      <div className="flex flex-col justify-between border-b border-line p-8 lg:border-b-0 lg:border-r lg:p-12">
        <Link to="/" className="flex items-center gap-2.5" data-testid="admin-login-home-link">
          <LogoMark className="h-8 w-8" />
          <span className="font-display text-sm font-black tracking-[0.2em]">SFUMO</span>
        </Link>
        <h1 className="my-16 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight lg:text-7xl">
          <MaskLine onLoad>Area</MaskLine>
          <MaskLine onLoad delay={0.1} className="text-acid">riservata.</MaskLine>
        </h1>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">sfushop · dashboard</p>
      </div>
      <div className="flex items-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="admin-login-form">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">
            <Lock className="h-3.5 w-3.5 text-acid" /> Accedi per gestire iscritti e ordini
          </p>
          <input required type="email" autoComplete="username" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-login-email-input" className={inputCls} />
          <input required type="password" autoComplete="current-password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-login-password-input" className={inputCls} />
          {error && <p data-testid="admin-login-error" className="font-mono text-xs text-red-400">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit-button" className="inline-flex h-12 items-center gap-3 rounded-full bg-acid px-7 font-mono text-xs uppercase tracking-[0.18em] text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {loading ? "Accesso…" : "Entra"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
